import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";

/**
 * EmailPreviewDialog - Shows email template preview before sending.
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {() => void} props.onClose - Close handler
 * @param {string} props.templateType - 'attendee_welcome' | 'coupon_success'
 * @param {Object|null} props.template - Template { subject, body } from GET_EMAIL_TEMPLATES
 * @param {Array} props.selectedRows - Selected rows (attendees or coupon claims)
 * @param {() => Promise<string>} props.onSend - Async send handler, returns message
 * @param {(message: string) => void} [props.onSuccess] - Called with result message on success
 * @param {string} props.title - Dialog title
 */
export default function EmailPreviewDialog({
  open,
  onClose,
  templateType,
  template,
  selectedRows = [],
  onSend,
  onSuccess,
  title = "Email Preview",
}) {
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);

  const { previewSubject, previewBody } = useMemo(() => {
    if (!template?.subject && !template?.body) {
      return { previewSubject: "", previewBody: "" };
    }
    let subj = template.subject || "";
    let body = template.body || "";
    const sample = selectedRows[0] || {};

    const replacements = {
      CouponNo: sample.couponNo || sample.coupon_no || "JOTOMBE2ND",
      couponNo: sample.couponNo || sample.coupon_no || "JOTOMBE2ND",
      TotalBill: sample.totalBill ?? sample.total_bill ?? "250.00",
      totalBill: sample.totalBill ?? sample.total_bill ?? "250.00",
      DiscountApplied: sample.discountApplied ?? sample.discount_applied ?? "25.00",
      discountApplied: sample.discountApplied ?? sample.discount_applied ?? "25.00",
      GrandTotal: sample.grandTotal ?? sample.finalAmount ?? "225.00",
      grandTotal: sample.grandTotal ?? sample.finalAmount ?? "225.00",
      ScannedBy: sample.scanName || sample.shopName || "Shop",
      ShopName: sample.shopName || sample.name || "Sample Shop",
      shop_name: sample.shopName || sample.name || "Sample Shop",
      ShopLocation: sample.shopLocation || "Sample Location",
      shop_location: sample.shopLocation || "Sample Location",
      ShopContact: sample.shopContact || "+968 1234 5678",
      shop_contact: sample.shopContact || "+968 1234 5678",
      shop_password: "********",
      ShopPassword: "********",
      Name: sample.name || (sample.parsedData?.name) || "John Doe",
      name: sample.name || (sample.parsedData?.name) || "John Doe",
      Email: sample.email || (sample.parsedData?.email) || "john@example.com",
      email: sample.email || (sample.parsedData?.email) || "john@example.com",
      FirstName: (sample.name || "John").split(" ")[0] || "John",
    };

    Object.entries(replacements).forEach(([key, value]) => {
      const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex1 = new RegExp(`\\$${escaped}\\b`, "g");
      const regex2 = new RegExp(`\\$\\{${escaped}\\}`, "g");
      subj = subj.replace(regex1, String(value)).replace(regex2, String(value));
      body = body.replace(regex1, String(value)).replace(regex2, String(value));
    });

    return { previewSubject: subj, previewBody: body };
  }, [template, selectedRows]);

  const handleSend = async () => {
    setError(null);
    setSending(true);
    try {
      const result = await onSend();
      onSuccess?.(typeof result === "string" ? result : "Emails sent");
      onClose();
    } catch (e) {
      setError(e?.message || "Failed to send emails");
    } finally {
      setSending(false);
    }
  };

  const hasTemplate = template && (template.subject || template.body);
  const canSend = hasTemplate && selectedRows.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {!hasTemplate ? (
          <Typography color="text.secondary">
            No {templateType === "attendee_welcome" ? "Attendee Welcome" : "Coupon Success"} template found. Please create one in Email Templates.
          </Typography>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Sending to {selectedRows.length} recipient(s). Preview (sample from first row):
            </Typography>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Subject:
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography>{previewSubject || "(empty)"}</Typography>
            </Paper>
            <Typography variant="subtitle2" gutterBottom>
              Body:
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <div dangerouslySetInnerHTML={{ __html: previewBody || "(empty)" }} />
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={handleSend}
          disabled={!canSend || sending}
        >
          {sending ? "Sending..." : `Send to ${selectedRows.length} recipient(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
